import type { Chat } from '@liniya/api-client';
import { BellOff, Pin } from 'lucide-react';
import { memo } from 'react';

import { Avatar } from '@/components/Avatar';
import { formatChatTimestamp, usePresenceStore } from '@liniya/client-core';

interface Props {
  chat: Chat;
  active: boolean;
  onOpen: (chatId: string) => void;
}

/**
 * One conversation in the sidebar.
 *
 * Memoised because a chat list is re-rendered on every incoming message, and
 * without it a busy account would repaint fifty rows to change one preview
 * line. Typing state is read from the store inside the row, so only the row
 * whose chat has someone typing re-renders.
 */
export const ChatRow = memo(function ChatRow({ chat, active, onOpen }: Props) {
  const typing = usePresenceStore((state) => state.typingByChat[chat.id]?.length ?? 0);
  const preview =
    typing > 0
      ? 'печатает…'
      : chat.membership.is_archived
        ? `В архиве · ${chat.last_message_preview}`
        : chat.last_message_preview;

  return (
    <button
      type="button"
      className={`chat-row${chat.kind === 'channel' ? ' channel-row' : ''}${active ? ' active' : ''}`}
      onClick={() => onOpen(chat.id)}
      aria-current={active ? 'true' : undefined}
    >
      <Avatar name={chat.title} kind={chat.kind} />
      <span className="chat-copy">
        <span className="chat-name">{chat.title}</span>
        <span className="chat-preview">{preview}</span>
      </span>
      <span className="chat-meta">
        <span className="chat-time">{formatChatTimestamp(chat.updated_at)}</span>
        <span className="chat-signals">
          {chat.membership.is_muted ? <BellOff size={13} aria-label="Уведомления отключены" /> : null}
          {chat.membership.is_pinned ? <Pin size={13} aria-label="Закреплён" /> : null}
          {chat.unread_count > 0 ? (
            <span className="badge" aria-label={`${chat.unread_count} непрочитанных`}>
              {chat.unread_count > 99 ? '99+' : chat.unread_count}
            </span>
          ) : null}
        </span>
      </span>
    </button>
  );
});
